import React, { useState } from 'react';
import Spinner from '../Spinner/Spinner';
import { Button } from 'react-bootstrap';
import PayixIframe from '../PayixIframe';
import debug from 'debug';
import { serverLogger } from '../../libs/datadog-logger/loggers/serverLogger';
import {
    classifyAddPaymentInfoFailure,
    getAddPaymentInfoUserMessage,
} from './addPaymentInfoErrorHandling';

const debugError = debug('auth:error');
const debugApi = debug('auth:payment-info:api');

// Payix generic /create-payment-token URL does not return an explicit type field.
// Distinguish by card-only fields (cardBrand / bin) that ACH payloads do not include.
// Verified against both Card and ACH postMessage payloads from staging.
const PAYMENT_TYPE_CARD = 1;
const PAYMENT_TYPE_ACH = 2;
const inferPaymentType = (tokenData) =>
    tokenData?.cardBrand || tokenData?.bin ? PAYMENT_TYPE_CARD : PAYMENT_TYPE_ACH;

const AddPaymentInfo = ({
    accountNumber,
    onAddNewPaymentChange,
    onTokenReceived,
    labelSize,
    mode,
}) => {
    const [serverError, setServerError] = useState(null);
    const [payixError, setPayixError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isDefault, setIsDefault] = useState(false);
    const [payixOutcome, setPayixOutcome] = useState(null); // null | 'success' | 'error'
    const handleCancelClick = () => {
        if (loading) return;
        setLoading(true);
        onAddNewPaymentChange('Cancel');
        setTimeout(() => {
            setLoading(false);
            setPayixError(null);
            setServerError(null);
            setPayixOutcome(null);
            setIsDefault(false);
        }, 2000);
    };

    const handlePayixTokenReceived = (tokenData) => {
        const paymentType = inferPaymentType(tokenData);
        debugApi('Payix token received: %O', {
            lastFour: tokenData.lastFourDigits,
            hasToken: !!tokenData.token,
            paymentType,
        });
        setPayixError(null);
        setServerError(null);
        setLoading(true);

        try {
            if (typeof onTokenReceived === 'function') {
                onTokenReceived({
                    ...tokenData,
                    paymentType,
                    shouldSetAsDefault: isDefault,
                });
            }
            onAddNewPaymentChange('AdditionCompleted', {
                Token: tokenData.token,
                LastFour: tokenData.lastFourDigits,
                PaymentType: paymentType,
                IsDefault: isDefault,
            });
            setPayixOutcome('success');
        } catch (error) {
            const failureType = classifyAddPaymentInfoFailure(error);
            const errorMessage = error?.response?.data?.Message || error?.message || 'Something went wrong';
            debugError('AddPaymentInfo - Token handling failed: %O', { failureType, errorMessage });
            serverLogger.error('payment.method.save_failed', {
                failureType,
                errorMessage,
                accountNumber,
                paymentType,
                mode,
                source: 'AddPaymentInfo.handlePayixTokenReceived',
            });
            setPayixOutcome('error');
            setServerError(getAddPaymentInfoUserMessage(failureType, errorMessage));
        } finally {
            setTimeout(() => setLoading(false), 1000);
        }
    };

    const handlePayixError = (error) => {
        debugError('Payix iframe error: %O', error);
        serverLogger.warn('payment.method.tokenization_failed', {
            errorMessage: error?.message,
            accountNumber,
            source: 'AddPaymentInfo.handlePayixError',
        });
        setPayixError(error.message || 'Failed to create payment token');
        setPayixOutcome('error');
        setLoading(false);
    };

    const closeButtonText =
        payixOutcome === 'success' ? 'Continue' : payixOutcome === 'error' ? 'Back' : 'Cancel';

    const isVerificationError =
        typeof serverError === 'string' &&
        serverError.toLowerCase().includes('could not confirm') &&
        serverError.toLowerCase().includes('payment method');

    const serverErrorBlock = serverError ? (
        <div
            className={`alert ${isVerificationError ? 'alert-warning' : 'alert-danger'}`}
            style={{ marginBottom: 10 }}
            role="alert"
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <strong>
                        {isVerificationError
                            ? 'Our payment system has not confirmed this payment method yet.'
                            : 'Unable to save payment method.'}
                    </strong>
                    <div>{serverError}</div>
                </div>
                <button
                    type="button"
                    className="close"
                    aria-label="Dismiss error"
                    onClick={() => setServerError(null)}
                    style={{ background: 'transparent', border: 0, marginLeft: '0.5rem' }}
                >
                    &times;
                </button>
            </div>
        </div>
    ) : null;

    const payixErrorBlock = payixError ? (
        <div className="alert alert-danger" style={{ marginBottom: 10 }} role="alert">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <strong>Payment Form Error</strong>
                    <div>{payixError}</div>
                </div>
                <button
                    type="button"
                    className="close"
                    aria-label="Dismiss error"
                    onClick={() => setPayixError(null)}
                    style={{ background: 'transparent', border: 0, marginLeft: '0.5rem' }}
                >
                    &times;
                </button>
            </div>
        </div>
    ) : null;

    const spinner = loading ? <Spinner /> : null;

    const labelSizeClass =
        typeof labelSize !== 'undefined'
            ? `col-sm-${labelSize} col-lg-${labelSize} col-form-label`
            : 'col-sm-3 col-lg-2 col-form-label';

    return (
        <div>
            {payixErrorBlock}
            {serverErrorBlock}

            <section style={{ marginBottom: '1rem' }}>
                <PayixIframe
                    accountNumber={accountNumber}
                    paymentType="both"
                    onToken={handlePayixTokenReceived}
                    onError={handlePayixError}
                    height={350}
                    zoom={1.5}
                    style={{ borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'hidden' }}
                    showLoader={true}
                />
            </section>

            {spinner}

            <div className="form-group">
                <div className="form-group row">
                    <div className={labelSizeClass}>
                        <label htmlFor="isDefault">Make Default Method?</label>
                    </div>
                    <div className="col-sm-7 col-form-control">
                        <label
                            htmlFor="isDefault"
                            style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontWeight: 'normal' }}
                        >
                            <input
                                type="checkbox"
                                id="isDefault"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                                disabled={loading}
                            />
                            <span>
                                If enrolled in Autopay, checking this box will change your Autopay method to this
                                card/account
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            <div className="form-group row">
                <div className={labelSizeClass}></div>
                <div className="col-sm-7">
                    <div className="button-group">
                        <Button type="button" onClick={handleCancelClick} bsStyle="basic" disabled={loading || payixOutcome === 'success'}>
                            {closeButtonText}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddPaymentInfo;
